import { useState } from 'react';
import { Typography, Table, Button, Modal, Form, Input, Tag, Dropdown, message, Avatar } from 'antd';
import { PlusOutlined, DownOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getAdmins, createAdmin, updateAdminStatus } from '@/services/admins';
import { maskEmail } from '@/utils/mask';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface Admin { id: string; name: string; email: string; role: string; status: string; createdAt: string; }

const STATUS_STYLES: Record<string, { color: string; bg: string }> = {
  ACTIVE:    { color: '#065f46', bg: '#d1fae5' },
  SUSPENDED: { color: '#991b1b', bg: '#fee2e2' },
  INACTIVE:  { color: '#6b7280', bg: '#f3f4f6' },
};

export default function ManageAdminsPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { data, isLoading } = useQuery({ queryKey: ['admins'], queryFn: getAdmins });

  const createMutation = useMutation({
    mutationFn: createAdmin,
    onSuccess: () => { message.success('Admin created'); queryClient.invalidateQueries({ queryKey: ['admins'] }); setCreateOpen(false); form.resetFields(); },
    onError: () => message.error('Failed to create admin'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updateAdminStatus(id, { status }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admins'] }); message.success('Status updated'); },
    onError: () => message.error('Failed to update status'),
  });

  const admins: Admin[] = (data as { data?: Admin[] })?.data ?? (Array.isArray(data) ? data as Admin[] : []);

  const columns = [
    {
      title: 'Name', dataIndex: 'name', key: 'name',
      render: (v: string) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{v}</span>,
    },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v: string) => <span style={{ color: '#64748b' }}>{maskEmail(v)}</span> },
    {
      title: 'Role', dataIndex: 'role', key: 'role',
      render: (v: string) => <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: '#6d28d9', background: '#ede9fe' }}>{v}</span>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: string) => {
        const s = STATUS_STYLES[v] ?? STATUS_STYLES.INACTIVE;
        return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: s.color, background: s.bg }}>{v}</span>;
      },
    },
    {
      title: 'Created', dataIndex: 'createdAt', key: 'createdAt',
      render: (v: string) => <span style={{ color: '#64748b' }}>{dayjs(v).format('DD MMM YYYY')}</span>,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, record: Admin) => (
        <Dropdown menu={{
          items: [{ key: 'ACTIVE', label: 'Activate' }, { key: 'SUSPENDED', label: 'Suspend' }, { key: 'INACTIVE', label: 'Deactivate' }].filter((i) => i.key !== record.status),
          onClick: ({ key }) => statusMutation.mutate({ id: record.id, status: key }),
        }}>
          <Button size="small">Action <DownOutlined /></Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Manage Admins</Typography.Title>
            <Tag color="red" style={{ borderRadius: 20, fontWeight: 700 }}>Super Admin</Tag>
          </div>
          <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Create and manage admin accounts</Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Create Admin
        </Button>
      </div>

      {isMobile ? (
        /* ── Mobile card list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : admins.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>No admins found</div>
          ) : admins.map((record) => {
            const s = STATUS_STYLES[record.status] ?? STATUS_STYLES.INACTIVE;
            const actionItems = [
              { key: 'ACTIVE', label: 'Activate' },
              { key: 'SUSPENDED', label: 'Suspend' },
              { key: 'INACTIVE', label: 'Deactivate' },
            ].filter((i) => i.key !== record.status);

            return (
              <div key={record.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <Avatar
                    size={38}
                    style={{ background: 'linear-gradient(135deg, #6d28d9, #6366f1)', fontWeight: 700, flexShrink: 0, fontSize: 15 }}
                  >
                    {record.name?.[0]?.toUpperCase()}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{record.name}</div>
                    <div style={{ color: '#64748b', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{maskEmail(record.email)}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: '#6d28d9', background: '#ede9fe' }}>
                      {record.role}
                    </span>
                    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: s.color, background: s.bg }}>
                      {record.status}
                    </span>
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>{dayjs(record.createdAt).format('DD MMM YYYY')}</span>
                  </div>
                  <Dropdown menu={{ items: actionItems, onClick: ({ key }) => statusMutation.mutate({ id: record.id, status: key }) }}>
                    <Button size="small" style={{ minHeight: 32 }}>Action <DownOutlined /></Button>
                  </Dropdown>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Desktop table ── */
        <Table
          columns={columns}
          dataSource={admins}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
        />
      )}

      <Modal title="Create Admin" open={createOpen} onCancel={() => { setCreateOpen(false); form.resetFields(); }} onOk={() => form.submit()} confirmLoading={createMutation.isPending}>
        <Form form={form} layout="vertical" onFinish={(values) => createMutation.mutate(values)} style={{ marginTop: 16 }} autoComplete="off">
          <Form.Item name="name" label="Full Name" rules={[{ required: true }]}><Input autoComplete="off" /></Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input autoComplete="off" /></Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true, min: 8 }]}><Input.Password autoComplete="new-password" /></Form.Item>
          <Form.Item name="confirmPassword" label="Confirm Password" dependencies={['password']} rules={[{ required: true },
            ({ getFieldValue }) => ({ validator(_, value) { return !value || getFieldValue('password') === value ? Promise.resolve() : Promise.reject(new Error('Passwords do not match')); } }),
          ]}><Input.Password autoComplete="new-password" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
